import type { z } from "zod";
import type { ToolName } from "@nabla/contract";
import type { commands, views } from "./routes.ts";

export type Method = "GET" | "POST" | "PATCH" | "DELETE";
export type Route<M extends Method = Method> = `${M} /api/${string}`;

export const viewPaths = {
  publico: { route: "GET /api/publico", public: true },
  saude: { route: "GET /api/saude", public: true },
  sessao: { route: "GET /api/sessao" },
  empresa: { route: "GET /api/visao/empresa" },
  conta: { route: "GET /api/visao/conta/{act}" },
  campanha: { route: "GET /api/visao/campanha/{id}" },
  conjunto: { route: "GET /api/visao/conjunto/{id}" },
  anuncio: { route: "GET /api/visao/anuncio/{id}" },
  mudanca: { route: "GET /api/mudancas/{p}" },
  simular: { route: "GET /api/mudancas/{p}/simular" },
  excecao: { route: "GET /api/mudancas/{p}/excecao" },
  simularObjeto: { route: "GET /api/objetos/{place}/{id}/simular" },
  mudancas: { route: "GET /api/mudancas" },
  registro: { route: "GET /api/registro" },
  resumo: { route: "GET /api/registro/resumo" },
  regras: { route: "GET /api/regras" },
  contas: { route: "GET /api/contas" },
  indice: { route: "GET /api/indice" },
  pessoas: { route: "GET /api/pessoas" },
  conversas: { route: "GET /api/conversas" },
  conversa: { route: "GET /api/conversas/{c}" },
  cartao: { route: "GET /api/agencia/cartao" },
} as const satisfies Record<string, { readonly route: Route<"GET">; readonly public?: true }>;

export type ChatUse = "cartao" | "navegador" | "interno" | { readonly ferramenta: ToolName };

export const commandPaths = {
  aceitarConvite: { route: "POST /api/convites/aceitar", keyed: false, public: true, chat: "interno" },
  entrar: { route: "POST /api/entrar", keyed: false, public: true, chat: "interno" },
  lerConvite: { route: "POST /api/convites/ler", keyed: false, public: true, chat: "interno" },
  linkDeEntrada: { route: "POST /api/sessao/link", keyed: true, chat: "navegador" },
  sair: { route: "POST /api/sair", keyed: false, chat: "navegador" },
  decidir: { route: "POST /api/mudancas/{p}/decisao", keyed: true, chat: "cartao" },
  ajustar: { route: "POST /api/mudancas/{p}/ajuste", keyed: true, chat: "cartao" },
  avaliar: { route: "POST /api/mudancas/{p}/avaliacao", keyed: true, chat: "cartao" },
  revisar: { route: "POST /api/mudancas/{p}/revisar", keyed: true, chat: "cartao" },
  conferir: { route: "POST /api/mudancas/{p}/conferir", keyed: true, chat: "cartao" },
  mudarRegra: { route: "POST /api/regras", keyed: true, chat: "cartao" },
  atualizar: { route: "POST /api/atualizar", keyed: true, chat: "cartao" },
  convidar: { route: "POST /api/pessoas/convites", keyed: true, chat: "cartao" },
  cancelarConvite: { route: "DELETE /api/pessoas/convites/{id}", keyed: true, chat: "cartao" },
  mudarPapel: { route: "PATCH /api/pessoas/{id}", keyed: true, chat: "cartao" },
  permitirRegras: { route: "POST /api/pessoas/{id}/regras", keyed: true, chat: "cartao" },
  encerrarSessoes: { route: "POST /api/pessoas/{id}/encerrar-sessoes", keyed: true, chat: "cartao" },
  desativar: { route: "POST /api/pessoas/{id}/desativar", keyed: true, chat: "cartao" },
  perguntar: { route: "POST /api/conversas", keyed: true, chat: "interno" },
  responder: { route: "POST /api/conversas/{c}/mensagens", keyed: true, chat: "interno" },
  reservar: { route: "POST /api/conversas/reservas", keyed: true, chat: "interno" },
  arquivarConversa: { route: "POST /api/conversas/{c}/arquivamento", keyed: true, chat: "interno" },
  reabrirConversa: { route: "DELETE /api/conversas/{c}/arquivamento", keyed: true, chat: "interno" },
  renomearConversa: { route: "PATCH /api/conversas/{c}", keyed: true, chat: "interno" },
  gerarTitulo: { route: "POST /api/conversas/{c}/titulo", keyed: true, chat: "interno" },
  arrumarConversa: { route: "PATCH /api/conversas/{c}/lugar", keyed: true, chat: "interno" },
  verConversa: { route: "POST /api/conversas/{c}/vista", keyed: false, chat: "interno" },
  guardarNota: { route: "POST /api/notas", keyed: true, chat: "cartao" },
  renomearNota: { route: "PATCH /api/notas/{n}", keyed: true, chat: "cartao" },
  retirarNota: { route: "DELETE /api/notas/{n}", keyed: true, chat: "cartao" },
  erroDoNavegador: { route: "POST /api/erros-do-navegador", keyed: false, chat: "interno" },
  mudar: { route: "POST /api/mudancas", keyed: true, chat: "cartao" },
  desfazer: { route: "POST /api/mudancas/{p}/desfazer", keyed: true, chat: "cartao" },
  cancelar: { route: "POST /api/mudancas/{p}/cancelar", keyed: true, chat: "cartao" },
  escreverRegra: { route: "POST /api/regras/escritas", keyed: true, chat: "cartao" },
  retirarRegra: { route: "DELETE /api/regras/escritas/{r}", keyed: true, chat: "cartao" },
  declararComparacao: { route: "POST /api/comparacoes", keyed: true, chat: { ferramenta: "propose-comparison" } },
  comecarComparacao: { route: "POST /api/comparacoes/{c}/comecar", keyed: true, chat: "cartao" },
  encerrarComparacao: { route: "DELETE /api/comparacoes/{c}", keyed: true, chat: "cartao" },
  parar: { route: "POST /api/contas/{act}/parar", keyed: true, chat: "cartao" },
  escopo: { route: "POST /api/contas/{act}/campanhas/{id}/escopo", keyed: true, chat: "cartao" },
} as const satisfies Record<string, { readonly route: Route<Exclude<Method, "GET">>; readonly keyed: boolean; readonly chat: ChatUse;
  readonly public?: true }>;

export type ViewName = keyof typeof viewPaths;
export type AnyViewName = keyof typeof views;
export type CommandName = keyof typeof commands;
export type ViewAnswer<K extends AnyViewName> = z.output<(typeof views)[K]["answer"]>;

const methods = ["GET", "POST", "PATCH", "DELETE"] as const;

export function routeParts(route: Route): { readonly method: Method; readonly path: string } {
  const method = methods.find((m) => route.startsWith(`${m} `));
  if (method === undefined) throw new Error(`route ${route} does not start with a method`);
  return { method, path: route.slice(method.length + 1) };
}

export type Api = typeof import("./answer-schemas.ts");
export const schemas = (): Promise<Api> => import("./answer-schemas.ts");
